import moment from 'moment'
import { useEffect, useState } from 'react'
import { Card } from 'react-bootstrap'

import useCallApi from '../../../hooks/useCallApi'
import Loading from '../../../templates/UI/Loading/Loading'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import Affiliate from '../../../templates/Affiliate/Affiliate'
import callAPI from '../../../helpers/callAPI'
import useDateRangeFilter from '../../../hooks/useDateRangeFilter'
import DateRangeFilter from '../../Reports/Filters/DateRangeFilter/DateRangeFilter'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import useQueryParams from '../../../hooks/useQueryParams'
import AffiliateDetailMatchingInformation from '../affiliateDetailMatchingInformation/AffiliateDetailMatchingInformation'

const AffiliateMatchingInformation = () => {
    document.title = 'Affiliate Matching Information'
    const { affiliateId, campaignId, advertiserId, creativeId, landingPageId, subid1, withAvatar, setWithAvatar, navigation, buildUrl, adminId, setAdminId, hideNetwork, setHideNetwork, businessAge, leadStatus, setLeadStatus } = useQueryParams()
    const location = useLocation()
    const history = useHistory()    
    const { selectedStartDate, selectedEndDate, quickDate, handleChangeStart, handleChangeEnd, handleQuickDate } = useDateRangeFilter()
    
    const [showModal, setShowModal] = useState(false)
    const [modalParams, setModalParams] = useState(null)
    const [checkedMatches, setCheckedMatches] = useState({}) // Store checked status

    useEffect(() => {
        let url = buildUrl(`${location.pathname}?startDate=${selectedStartDate}&endDate=${selectedEndDate}`)
        history.push(url)
    }, [selectedStartDate, selectedEndDate])

    const { data, dataLoaded, refetch } = useCallApi(`compliance/affiliateMatchingInformation?startDate=${selectedStartDate}&endDate=${selectedEndDate}`, 'GET', {}, [selectedStartDate, selectedEndDate])

    // Initialize checked matches from API data
    useEffect(() => {
        if (dataLoaded && data?.success && data?.matchingInformation) {
            const initialChecked = {}
            data.matchingInformation.forEach(row => {
                if (row.matched_key && row.matched_value && row.reviewed === 'Y') {
                    const matchId = `${row.affiliate_id}-${row.matched_key}-${row.matched_value.encrypted}`
                    initialChecked[matchId] = true
                }
            })
            setCheckedMatches(initialChecked)
        }
    }, [dataLoaded, data])

    const handleOpenModal = (key, encryptedValue, affiliateId) => {
        setModalParams({ key, value: encryptedValue, affiliate_id: affiliateId })
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setModalParams(null)
    }

    const handleCheckboxChange = async (affiliateId, key, encryptedValue, isChecked) => {
        const matchId = `${affiliateId}-${key}-${encryptedValue}`
        
        // Update local state immediately for UI feedback
        if (isChecked) {
            setCheckedMatches(prev => ({ ...prev, [matchId]: true }))
        } else {
            setCheckedMatches(prev => {
                const newChecked = { ...prev }
                delete newChecked[matchId]
                return newChecked
            })
        }

        // Call API to persist the change
        const reviewed = isChecked ? 'Y' : 'N'
        try {
            await callAPI(`compliance/affiliateMatchingInformation/${affiliateId}/review/${reviewed}`, 'PUT', { 
                key, 
                value: encryptedValue 
            })
        } catch (error) {
            console.error('Error updating review status:', error)
            // Revert local state on error
            if (isChecked) {
                setCheckedMatches(prev => {
                    const newChecked = { ...prev }
                    delete newChecked[matchId]
                    return newChecked
                })
            } else {
                setCheckedMatches(prev => ({ ...prev, [matchId]: true }))
            }
        }
    }

    const isMatchChecked = (affiliateId, key, encryptedValue) => {
        const matchId = `${affiliateId}-${key}-${encryptedValue}`
        return !!checkedMatches[matchId]
    }

    const transformData = (rawData) => {
        // Calculate global counts for each key-value combination
        const globalCounts = {}

        rawData.forEach(row => {
            if (row.matched_key && row.matched_value) {
                const countKey = `${row.matched_key}:${row.matched_value.decrypted}`
                globalCounts[countKey] = (globalCounts[countKey] || 0) + 1
            }
        })

        // Group by affiliate_id
        const groupedData = {}
        
        rawData.forEach(row => {
            const affiliateId = row.affiliate_id
            
            if (!groupedData[affiliateId]) {
                groupedData[affiliateId] = {
                    affiliate_id: row.affiliate_id,
                    affiliate_name: row.affiliate_name,
                    status: row.status,
                    aff_priority: row.aff_priority,
                    country_code: row.country_code,
                    registration_date: row.registration_date,
                    matches: {} // Store all matches by key
                }
            }
            
            if (row.matched_key && row.matched_value) {
                if (!groupedData[affiliateId].matches[row.matched_key]) {
                    groupedData[affiliateId].matches[row.matched_key] = []
                }
                
                const countKey = `${row.matched_key}:${row.matched_value}`
                groupedData[affiliateId].matches[row.matched_key].push({
                    value: row.matched_value.decrypted,
                    encryptedValue: row.matched_value.encrypted,
                    count: row.matching
                })
            }
        })

        return Object.values(groupedData)
    }

    const getUniqueKeys = (rawData) => {
        const keys = new Set()
        rawData.forEach(row => {
            if (row.matched_key) {
                keys.add(row.matched_key)
            }
        })
        return Array.from(keys)
    }

    const getReportOutput = () => {
        const transformedData = transformData(data.matchingInformation)
        const uniqueKeys = getUniqueKeys(data.matchingInformation)

        let columns = [
            {
                Header: 'Affiliate ID',
                accessor: 'affiliate_id',
                Cell: (tableData) => {
                    return <>{tableData.row.original.affiliate_id}</>
                },
            },
            {
                Header: 'Affiliate',
                accessor: 'affiliate_name',
                Cell: (tableData) => {
                    return <>{<Affiliate aid={tableData.row.original.affiliate_id} name={tableData.row.original.affiliate_name} status={tableData.row.original.status} priority={tableData.row.original.aff_priority} />}</>
                },
            },
            {
                Header: 'Country',
                accessor: 'country_code',
                Cell: (tableData) => {
                    return <>{tableData.row.original.country_code}</>
                },
            },
            {
                Header: 'Registration Date',
                accessor: 'registration_date',
                Cell: (tableData) => {
                    return <>{moment.utc(tableData.row.original.registration_date).local().format('YYYY-MM-DD HH:mm:ss')}</>
                },
            },

        ]
        uniqueKeys.forEach(key => {
            columns.push({
                Header: key,
                accessor: key,
                sortType: (rowA, rowB) => {
                    const matchesA = rowA.original.matches[key]
                    const matchesB = rowB.original.matches[key]
                    const countA = matchesA ? matchesA.reduce((sum, match) => sum + match.count, 0) : 0
                    const countB = matchesB ? matchesB.reduce((sum, match) => sum + match.count, 0) : 0
                    return countA - countB
                },

                //if match count over 10
                conditionalClassName: (value, row) => {
                    const matches = row.matches[key]
                    if (matches) {
                        // Check if all matches are reviewed
                        const allReviewed = matches.every(match => 
                            isMatchChecked(row.affiliate_id, key, match.encryptedValue)
                        )
                        if (allReviewed) {
                            return 'bgSuccess25'
                        }
                        const totalCount = matches.reduce((sum, match) => sum + match.count, 0)
                        if (totalCount >= 10) {
                            return 'bgDanger25'
                        } else {
                            return 'bgWarning25'
                        }
                    }
                    return ''
                },
                Cell: (tableData) => {
                    const row = tableData.row.original
                    const matches = row.matches[key]
                    
                    if (matches && matches.length > 0) {
                        return (
                            <div>
                                {matches.map((match, index) => (
                                    <div key={index} className="mb-1">
                                        <a 
                                            href="#" 
                                            onClick={(e) => {
                                                e.preventDefault()
                                                handleOpenModal(key, match.encryptedValue, row.affiliate_id)
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {match.value}</a> ({match.count})
                                        
                                    </div>
                                ))}
                            </div>
                        )
                    }
                    return <>-</>
                },
            })
        })

        return (
            <ReportTable
                data={transformedData}
                columns={columns}
            />
        )
    }

    return (
        <div>
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Affiliate Matching Information</h1>

            </div>
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex align-items-center">
                            <h5 className="mb-h1-dark mr-2">Affiliate Matching Information</h5>
                            <DateRangeFilter quickDate={quickDate} selectedStartDate={selectedStartDate} selectedEndDate={selectedEndDate} handleChangeStart={handleChangeStart} handleChangeEnd={handleChangeEnd} handleQuickDate={handleQuickDate} />
                        </Card.Header>
                        <Card.Body className="p-0">
                            {dataLoaded ? (
                                data.success ? (
                                    getReportOutput()
                                ) : (
                                    <div className="alert alert-danger" role="alert">
                                        {data.message}
                                    </div>
                                )
                            ) : (
                                <Loading />
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {modalParams && (
                <AffiliateDetailMatchingInformation 
                    queryKey={modalParams.key}
                    queryValue={modalParams.value}
                    queryAffiliateId={modalParams.affiliate_id}
                    showModal={showModal}
                    handleCloseModal={handleCloseModal}
                    isReviewed={isMatchChecked(modalParams.affiliate_id, modalParams.key, modalParams.value)}
                    onReviewChange={(isChecked) => handleCheckboxChange(modalParams.affiliate_id, modalParams.key, modalParams.value, isChecked)}
                />
            )}
        </div>
    )
}

export default AffiliateMatchingInformation
